import React, { useState, useEffect } from 'react';
import { getPartners, createPartner, updatePartner, deletePartner, togglePartnerStatus } from '../../services/api';
import { getCategories } from '../../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import PartnerForm from '../forms/PartnerForm';
import toast from 'react-hot-toast';

const PartnersModule = () => {
  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const columns = ['Logo', 'Name', 'Type', 'Category', 'Website', 'Status', 'Actions'];

  useEffect(() => {
    fetchPartners();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      let categoriesArray = [];
      if (response && response.data && Array.isArray(response.data)) {
        // Filter only partner categories (type === 'partner')
        categoriesArray = response.data.filter(cat => cat.type === 'partner');
      } else if (response && Array.isArray(response)) {
        categoriesArray = response.filter(cat => cat.type === 'partner');
      }
      setCategories(categoriesArray);
      console.log('Partner categories loaded:', categoriesArray);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const response = await getPartners();
      const partnersData = response.data || response || [];
      setPartners(partnersData);
      console.log('Partners loaded:', partnersData.length);
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast.error('Error fetching partners');
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this partner?')) {
      try {
        await deletePartner(id);
        toast.success('Partner deleted successfully');
        fetchPartners();
      } catch (error) {
        toast.error('Error deleting partner');
      }
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await togglePartnerStatus(id);
      toast.success(`Partner ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchPartners();
    } catch (error) {
      toast.error('Error toggling partner status');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    fetchCategories(); // Refresh categories when modal closes
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const getCategoryIcon = (categoryName) => {
    const category = categories.find(c => c.name === categoryName);
    return category?.icon || '🤝';
  };

  if (loading) {
    return <LoadingSpinner message="Loading partners..." />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ color: '#0A192F', fontSize: '1.5rem', marginBottom: '0.25rem' }}>Partners Management</h2>
          <p style={{ color: '#5A6E7F' }}>Manage your partner organizations and collaborations</p>
          <p style={{ color: '#2C7A7B', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            <i className="fas fa-handshake"></i> Total Partners: {partners.length} | Categories: {categories.length}
          </p>
        </div>
        <button 
          onClick={() => { setEditingItem(null); setShowModal(true); }} 
          style={{ padding: '0.6rem 1.2rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 500 }}
        >
          <i className="fas fa-plus" /> Add New Partner
        </button>
      </div>

      {partners.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: '#F7F9FC', borderRadius: '12px', border: '2px dashed #E2E8F0' }}>
          <i className="fas fa-handshake" style={{ fontSize: '4rem', color: '#CBD5E1', marginBottom: '1rem' }} />
          <p style={{ color: '#5A6E7F', marginBottom: '1rem', fontSize: '1.1rem' }}>No partners found</p>
          <button 
            onClick={() => { setEditingItem(null); setShowModal(true); }} 
            style={{ padding: '0.6rem 1.2rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}
          >
            <i className="fas fa-plus"></i> Create Your First Partner
          </button>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, color: '#0A192F' }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {partners.map((partner) => (
                <tr key={partner._id} style={{ borderBottom: '1px solid #E2E8F0', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#F7F9FC'} onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                  <td style={{ padding: '1rem' }}>
                    {partner.logo ? (
                      <img src={getFullImageUrl(partner.logo)} alt={partner.name} style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} 
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                    ) : null}
                    <div style={{ width: '50px', height: '50px', background: '#F7F9FC', borderRadius: '8px', display: partner.logo ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fas fa-building" style={{ color: '#CBD5E1', fontSize: '1.5rem' }} />
                    </div>
                  </td>
                  <td style={{ padding: '1rem', fontWeight: 500, color: '#0A192F' }}>{partner.name}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{ background: '#E3F2FD', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.75rem', color: '#2C7A7B', fontWeight: 500 }}>
                      {partner.type}
                    </span>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {partner.category ? (
                      <span style={{ 
                        background: '#F3E5F5', 
                        padding: '0.25rem 0.75rem', 
                        borderRadius: '20px', 
                        fontSize: '0.75rem', 
                        color: '#7B1FA2', 
                        fontWeight: 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.3rem'
                      }}>
                        <span>{getCategoryIcon(partner.category)}</span>
                        {partner.category}
                      </span>
                    ) : (
                      <span style={{ color: '#94A3B8', fontSize: '0.75rem' }}>No category</span>
                    )}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {partner.website ? (
                      <a href={partner.website} target="_blank" rel="noopener noreferrer" style={{ color: '#2C7A7B', textDecoration: 'none', fontSize: '0.85rem' }}>
                        {partner.website.replace('https://', '').replace('http://', '').substring(0, 30)}
                        <i className="fas fa-external-link-alt" style={{ fontSize: '0.7rem', marginLeft: '0.3rem' }} />
                      </a>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleToggle(partner._id, partner.isActive)} 
                      style={{ 
                        padding: '0.25rem 0.75rem', 
                        borderRadius: '20px', 
                        border: 'none', 
                        background: partner.isActive ? '#D4EDDA' : '#F8D7DA', 
                        color: partner.isActive ? '#155724' : '#721C24', 
                        cursor: 'pointer', 
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}
                    >
                      {partner.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <button 
                      onClick={() => handleEdit(partner)} 
                      style={{ 
                        padding: '0.35rem 0.8rem', 
                        background: '#2C7A7B', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer', 
                        marginRight: '0.5rem',
                        fontSize: '0.75rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#236B6C'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#2C7A7B'}
                    >
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(partner._id)} 
                      style={{ 
                        padding: '0.35rem 0.8rem', 
                        background: '#DC2626', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#B91C1C'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#DC2626'}
                    >
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <PartnerForm 
          editingItem={editingItem} 
          categories={categories}
          onClose={handleCloseModal} 
          onRefresh={fetchPartners} 
        />
      )}
    </div>
  );
};

export default PartnersModule;